import type { Connection, ConnectionSourcePackageTypeEnum } from './connection.types';

export type EmployerConnection = Pick<
  Connection,
  '@id' | '@type' | 'connectionId' | 'connectionName' | 'connectionType' | 'product'
>;

export type EmployerNote = {
  noteId: string;
  text: string;
  employerId: Employer['employerId'];
  createdAt: string;
  updatedAt: string;
};

export type Employer = {
  '@id': string;
  '@type': string;
  address: string;
  chamberOfCommerce: string;
  city: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
  employerEmail: string;
  employerId: string;
  employerName: string;
  employerPhone: string;
  internalNumber?: string;
  owner: string;
  ownerId: string;
  parentId: string | null;
  payrollTaxNumber: string;
  sectorCode: string;
  status: EmployerStatus;
  zipCode: string;
  connections: Array<EmployerConnection>;
  subEmployers: Array<Employer>;
  isPensioenbijIntegration?: boolean;
  tMoments: [];
};

export enum EmployerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  UNDER_CONSTRUCTION = 'underConstruction',
}

export type EmployerDetailNote = {
  noteId: string;
  text: string;
  createdAt: string;
  updatedAt: string;
};

export type EmployerDetailOwner = {
  ownerId: string;
  ownerName: string;
  status: boolean;
  address: string | null;
  city: string | null;
  zipCode: string | null;
  ownerEmail: string | null;
  ownerPhone: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  logo: string | null;
  instance: string | null;
  kvkNumber: string | null;
  insurerAccesses: Record<string, string[]> | null;
  parent: (Omit<EmployerDetailOwner, 'parent' | 'subOwners'> & { parent: null; subOwners: [] }) | null;
  subOwners: Array<Omit<EmployerDetailOwner, 'parent' | 'subOwners'> & { parent: null; subOwners: [] }>;
};

export type EmployerDetailConnectionPackage = {
  packageType: ConnectionSourcePackageTypeEnum | string;
  settings: Record<string, unknown> | null;
};

export type EmployerDetailConnection = {
  connectionId: string;
  connectionName: string;
  packages: EmployerDetailConnectionPackage[];
  insurer: {
    insurerId: string;
    insurerName: string;
    type: string;
    status: boolean;
    address: string | null;
    city: string | null;
    zipCode: string | null;
    email: string | null;
    phone: string | null;
    portalLinks: Record<string, string>;
    multipleProductType: boolean | null;
  } | null;
  product: {
    productId: string;
    productName: string;
    type: string;
    status: boolean;
    description: string | null;
    pensionScheme: string | null;
    productType: string[];
  } | null;
};

export type EmployerDetail = {
  employerId: string;
  employerName: string;
  status: string;
  parentId: string | null;
  sectorCode: string | null;
  chamberOfCommerce: string | null;
  payrollTaxNumber: string | null;
  address: string | null;
  city: string | null;
  zipCode: string | null;
  employerEmail: string | null;
  employerPhone: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  internalNumber: string | null;
  agent: string | null;
  legalEntity: string | null;
  sbiCode: number | null;
  isPensioenbijIntegration: boolean;
  tMoments: number[] | null;
  owner: EmployerDetailOwner;
  connections: EmployerDetailConnection[];
  notes: EmployerDetailNote | null;
  subEmployers: Array<Omit<EmployerDetail, 'subEmployers'> & { subEmployers: [] }>;
};

export type EmployerFile = {
  fileId: string;
  employerId: Employer['employerId'];
  employeeId: string;
  title: string;
  encodedFilename: string;
  createdAt: string;
  updatedAt: string;
};
